import React from "react"
import { ConditionalRenderContainer } from "@mallardbay/lib-react-components"

import Seo from "~components/shared/seo"
import Booking from "~components/pages/book/book"
import BookingSummary from "~components/pages/book/booking-summary"
import LoadingOrErrorContainer from "~components/shared/todo-lib-react-components/loading-or-error-container"
import { useBookTrip } from "~components/pages/book/book-trip-provider"
import type { QuoteFieldsFragment } from "~graphql/generated/graphql"
import COPY from "~config/copy-constants"
import AnimatedButton from "~components/shared/todo-lib-react-components/animated-button"
import type { PaymentMethodState } from "~utils/use-payment-method-state"
import { useCheckoutFlow } from "~components/pages/listing/checkout/checkout-flow/checkout-flow-provider"

import TripSummaryBarWithDrawer from "../listing/checkout/trip-summary-bar-with-drawer"

export default function BookContent() {
    const {
        quote,
        listing,
        isLoadingQuote,
        quoteError,
        paymentMethodState,
        createBooking,
        isLoadingCreateBooking,
    } = useBookTrip()
    const { isWidget } = useCheckoutFlow()

    const { isReadyToSubmit } = getIsReadyToSubmit({
        paymentMethodState,
        quote,
    })

    return (
        <LoadingOrErrorContainer
            isLoading={isLoadingQuote && !quote}
            error={quoteError}
            shouldRenderChildren={Boolean(quote) && Boolean(listing)}
        >
            <ConditionalRenderContainer shouldRender={!isWidget}>
                <Seo
                    title={`Book ${listing?.title}`}
                    image={listing?.cover_image ?? ""}
                />
            </ConditionalRenderContainer>
            <Booking
                listing={listing!}
                quote={quote!}
                isQuoteLoading={isLoadingQuote}
                renderNavButtons={() =>
                    renderNavButtons({
                        isLoadingCreateBooking,
                        isReadyToSubmit,
                        createBooking,
                        paymentMethodState,
                    })
                }
            />
            <TripSummaryBarWithDrawer
                total={quote?.total}
                isLoading={isLoadingQuote}
                renderNavButtons={() =>
                    renderNavButtons({
                        isLoadingCreateBooking,
                        isReadyToSubmit,
                        createBooking,
                        paymentMethodState,
                        isSmall: true,
                    })
                }
            >
                <BookingSummary
                    listing={listing}
                    quote={quote}
                    isQuoteLoading={isLoadingQuote}
                />
            </TripSummaryBarWithDrawer>
        </LoadingOrErrorContainer>
    )
}

function getIsReadyToSubmit({
    paymentMethodState,
    quote,
}: {
    paymentMethodState: ReturnType<typeof useBookTrip>["paymentMethodState"]
    quote?: QuoteFieldsFragment | null
}) {
    const { paymentMethodId, isExternalPaymentMethod } = paymentMethodState
    const isPayWithCheckEnabled = Boolean(quote?.should_allow_external_payment)

    // This check is redundant. Unless there is a bug in the payment components logic,
    // isExternalPaymentMethod should never be true when pay with check is disabled.
    // If the situation does happen, the quote query and createBooking mutation will return errors.
    const isPayingWithCheck = isPayWithCheckEnabled && isExternalPaymentMethod
    const isReadyToSubmit = Boolean(paymentMethodId) || isPayingWithCheck

    return { isReadyToSubmit }
}

function renderNavButtons({
    isLoadingCreateBooking,
    isReadyToSubmit,
    createBooking,
    paymentMethodState,
    isSmall,
}: {
    isLoadingCreateBooking: boolean
    isReadyToSubmit: boolean
    createBooking: (paymentMethodState: PaymentMethodState) => Promise<void>
    paymentMethodState: PaymentMethodState
    isSmall?: boolean
}) {
    const isDisabled = isLoadingCreateBooking || !isReadyToSubmit
    const shouldAnimate = !isLoadingCreateBooking && isReadyToSubmit
    return (
        <AnimatedButton
            label={COPY.REQUEST_TO_BOOK}
            onClick={() => {
                void createBooking(paymentMethodState)
            }}
            isLoading={isLoadingCreateBooking}
            shouldAnimate={shouldAnimate}
            isDisabled={isDisabled}
            isSmall={isSmall}
        />
    )
}
